import { useState, useCallback } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  StatusBadge, LoadingState, ErrorState, ApiModeBanner, Toggle, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { Photographer, PhotographerStatus, PhotographerGender } from "../../../store/types";

const NOW = () => new Date().toISOString();
const STATUSES: PhotographerStatus[] = ["active", "inactive", "on_leave", "suspended"];

function emptyPhotographer(): Photographer {
  return {
    id: `ph-${Date.now()}`,
    photographer_code: "",
    display_name: "",
    legal_name: "",
    email: "",
    phone: "",
    country_code: "JP",
    city_base: "",
    languages: [],
    status: "active",
    portfolio_url: "",
    notes: "",
    is_active: true,
    gender: "prefer_not_to_say",
    created_at: NOW(),
    updated_at: NOW(),
  };
}

// ─── Language chip display ────────────────────────────────────────────────────

function LangChips({ langs }: { langs: string[] }) {
  if (!langs.length) return <span className="text-[#2C2C2C]/30 text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {langs.map((l) => (
        <span key={l} className="px-2 py-0.5 bg-[#C8B8A6]/15 text-[#2C2C2C]/60 rounded-full text-xs font-light uppercase">
          {l}
        </span>
      ))}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<PhotographerStatus, string> = {
  active: "bg-emerald-50 text-emerald-600",
  inactive: "bg-[#D9D9D9]/50 text-[#2C2C2C]/40",
  on_leave: "bg-amber-50 text-amber-600",
  suspended: "bg-red-50 text-red-500",
};

function PhStatusBadge({ status, label }: { status: PhotographerStatus; label: string }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${STATUS_STYLES[status]}`}>
      {label}
    </span>
  );
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────

const GENDERS: { value: PhotographerGender; label: string }[] = [
  { value: "prefer_not_to_say", label: "不提供" },
  { value: "male", label: "男性" },
  { value: "female", label: "女性" },
];

function EditModal({
  form, onChange, onClose, onSave, saving, isNew,
}: {
  form: Photographer; onChange: (p: Photographer) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
}) {
  const t = useAdminT();
  const set = (field: keyof Photographer, value: unknown) => onChange({ ...form, [field]: value });

  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.photographers.add_photographer") : t("tabifotoAdmin.photographers.edit_photographer")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographers.photographer_code")} required>
          <input className={inputCls} value={form.photographer_code} onChange={(e) => set("photographer_code", e.target.value)} placeholder="KY-001" />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographers.status")} required>
          <select className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value as PhotographerStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>{t(`tabifotoAdmin.photographers.status_${s}`)}</option>
            ))}
          </select>
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographers.display_name")} required>
          <input className={inputCls} value={form.display_name} onChange={(e) => set("display_name", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographers.legal_name")}>
          <input className={inputCls} value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographers.email")}>
          <input className={inputCls} type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographers.phone")}>
          <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographers.country_code")}>
          <input className={inputCls} value={form.country_code} onChange={(e) => set("country_code", e.target.value)} placeholder="JP" />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographers.city_base")} required>
          <input className={inputCls} value={form.city_base} onChange={(e) => set("city_base", e.target.value)} placeholder="Kyoto" />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField label={t("tabifotoAdmin.photographers.languages")}>
          <input
            className={inputCls}
            value={form.languages.join(",")}
            onChange={(e) => set("languages", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            placeholder="zh,en,ja"
          />
        </FormField>
        <FormField label={t("tabifotoAdmin.photographers.gender_label")}>
          <select
            className={inputCls}
            value={form.gender ?? "prefer_not_to_say"}
            onChange={(e) => set("gender", e.target.value as PhotographerGender)}
          >
            {GENDERS.map((g) => (
              <option key={g.value} value={g.value}>{g.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-[#2C2C2C]/30 mt-1">{t("tabifotoAdmin.photographers.gender_note")}</p>
        </FormField>
      </div>

      <FormField label={t("tabifotoAdmin.photographers.portfolio_url")}>
        <input className={inputCls} value={form.portfolio_url} onChange={(e) => set("portfolio_url", e.target.value)} placeholder="https://..." />
      </FormField>

      <FormField label={t("tabifotoAdmin.photographers.notes")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.notes} onChange={(e) => set("notes", e.target.value)} maxLength={500} />
      </FormField>

      <div className="flex items-center gap-3 mt-1">
        <Toggle checked={form.is_active} onChange={(v) => set("is_active", v)} />
        <span className="text-xs text-[#2C2C2C]/50">
          {form.is_active ? t("tabifotoAdmin.common.active") : t("tabifotoAdmin.common.inactive")}
        </span>
      </div>
    </AdminModal>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminPhotographersPage() {
  const t = useAdminT();
  const { state, status, isPhotographersSupabaseMode, upsertPhotographer, removePhotographer, refetchPhotographers } = useAdminData();
  const toast = useToast();

  const [editItem, setEditItem] = useState<Photographer | null>(null);
  const [saving, setSaving] = useState(false);
  const [deactivateId, setDeactivateId] = useState<string | null>(null);
  const [filterActive, setFilterActive] = useState<"all" | "active" | "inactive">("all");

  const statusLabel = useCallback((s: PhotographerStatus) => t(`tabifotoAdmin.photographers.status_${s}`), [t]);

  const filtered = [...state.photographers]
    .filter((ph) => {
      if (filterActive === "active") return ph.is_active;
      if (filterActive === "inactive") return !ph.is_active;
      return true;
    })
    .sort((a, b) => a.display_name.localeCompare(b.display_name));

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertPhotographer(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!deactivateId) return;
    try {
      await removePhotographer(deactivateId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeactivateId(null); }
  };

  const FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.photographer_availability.filter_all") },
    { key: "active" as const, label: t("tabifotoAdmin.common.active") },
    { key: "inactive" as const, label: t("tabifotoAdmin.common.inactive") },
  ];

  const headers = [
    t("tabifotoAdmin.photographers.col_code"),
    t("tabifotoAdmin.photographers.col_name"),
    t("tabifotoAdmin.photographers.col_legal_name"),
    t("tabifotoAdmin.photographers.col_city_base"),
    t("tabifotoAdmin.photographers.col_languages"),
    t("tabifotoAdmin.photographers.col_status"),
    t("tabifotoAdmin.photographers.col_active"),
    t("tabifotoAdmin.photographers.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.photographers.title")}
      subtitle={t("tabifotoAdmin.photographers.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyPhotographer())}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.photographers.add_photographer")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isPhotographersSupabaseMode} />

      <div className="flex items-center justify-between mb-5">
        <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg">
          {FILTER_TABS.map((tab) => (
            <button key={tab.key} onClick={() => setFilterActive(tab.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterActive === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-[#2C2C2C]/40 font-light">
          {t("tabifotoAdmin.photographers.all_count").replace("{{count}}", String(filtered.length))}
        </span>
      </div>

      {status.photographers.loading ? <LoadingState /> :
        status.photographers.error ? <ErrorState message={status.photographers.error} onRetry={refetchPhotographers} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.photographers.no_photographers")}>
            {filtered.map((ph) => (
              <tr key={ph.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
                <td className="px-5 py-3"><span className="text-xs font-mono text-[#2C2C2C]/50">{ph.photographer_code}</span></td>
                <td className="px-5 py-3">
                  <div>
                    <p className="text-sm font-light text-[#2C2C2C]">{ph.display_name}</p>
                    <p className="text-xs text-[#2C2C2C]/40 mt-0.5">{ph.email}</p>
                  </div>
                </td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{ph.legal_name || "—"}</td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">{ph.city_base || "—"}</td>
                <td className="px-5 py-3"><LangChips langs={ph.languages} /></td>
                <td className="px-5 py-3"><PhStatusBadge status={ph.status} label={statusLabel(ph.status)} /></td>
                <td className="px-5 py-3"><StatusBadge active={ph.is_active} /></td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditItem({ ...ph })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button onClick={() => setDeactivateId(ph.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                      <i className="ri-user-unfollow-line text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.photographers.some((p) => p.id === editItem.id)} />
      )}

      {deactivateId && (
        <ConfirmDialog
          title={t("tabifotoAdmin.photographers.deactivate_confirm")}
          message={t("tabifotoAdmin.photographers.deactivate_desc")}
          confirmLabel={t("tabifotoAdmin.common.deactivate")}
          onConfirm={handleDeactivate} onCancel={() => setDeactivateId(null)} />
      )}
    </AdminLayout>
  );
}
